// src/components/ProcessSequencer.jsx

import React from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// A single draggable item in the sequence
const SortableItem = ({ id, name, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: '8px 12px',
    border: '1px solid var(--jq-border-medium, #ccc)',
    borderRadius: '4px',
    marginBottom: '4px',
    backgroundColor: 'var(--jq-bg-card, white)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <span>{name}</span>
      <button 
        type="button" 
        className="btn btn-sm btn-outline-danger" 
        onClick={() => onRemove(name)}
        onPointerDown={(e) => {
          e.stopPropagation(); 
        }}
      >
        X
      </button>
    </div>
  );
};

// The main sequencer component
export default function ProcessSequencer({ allProcesses, selectedSteps, setSelectedSteps }) {
  const sensors = useSensors(useSensor(PointerSensor));

  const availableProcesses = allProcesses.filter(p => !selectedSteps.some(step => step.name === p));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSelectedSteps((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddStep = (stepName) => {
    const newItem = { id: `step-${Date.now()}-${stepName}`, name: stepName };
    setSelectedSteps(prev => [...prev, newItem]);
  };
  
  const handleRemoveStep = (stepName) => {
    setSelectedSteps(prev => prev.filter(step => step.name !== stepName));
  };

  return (
    <div className="row">
      <div className="col-md-6">
        <h6>Available Processes</h6>
        <div className="border rounded p-2" style={{ minHeight: '200px' }}>
          {availableProcesses.map(proc => (
            <button key={proc} type="button" className="btn btn-sm btn-outline-secondary m-1" onClick={() => handleAddStep(proc)}>
              {proc} +
            </button>
          ))}
        </div>
      </div>
      <div className="col-md-6">
        <h6>Selected Sequence (Drag to Reorder)</h6>
        <div className="border rounded p-2" style={{ minHeight: '200px', backgroundColor: 'var(--jq-bg-subtle, #f8f9fa)' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={selectedSteps} strategy={verticalListSortingStrategy}>
              {selectedSteps.map(step => (
                <SortableItem 
                  key={step.id} 
                  id={step.id} 
                  name={step.name} 
                  onRemove={handleRemoveStep}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
}